import { useEffect, useState } from "react";
import { Platform } from "react-native";

/** IME: hysteresis “open” when overlap exceeds this (px). */
const OPEN_THRESHOLD_PX = 72;
/** Stay “open” until overlap drops below this (px). */
const CLOSE_THRESHOLD_PX = 44;

export type WebVirtualKeyboardState = {
  /** Hysteresis-stable “keyboard open” from `visualViewport` only. */
  keyboardOpen: boolean;
  /** Raw bottom overlap (px); same timing as `keyboardOpen` updates. */
  overlapPx: number;
};

function readKeyboardOverlapPx(): number {
  if (typeof window === "undefined" || !window.visualViewport) {
    return 0;
  }
  const vv = window.visualViewport;
  return Math.max(0, window.innerHeight - vv.height - vv.offsetTop);
}

const INITIAL_STATE: WebVirtualKeyboardState = { keyboardOpen: false, overlapPx: 0 };

/**
 * Web-only: `visualViewport` vs `window.innerHeight` overlap (IME / browser chrome).
 * Returns **`keyboardOpen`** (hysteresis) and **`overlapPx`** (live) so you can combine with
 * input **focus** for layout: e.g. in-flow footer when `keyboardOpen || fieldFocused`, and
 * treat keyboard as **gone** only when `!keyboardOpen && overlapPx` is small (stricter than blur alone).
 */
export function useWebVirtualKeyboardOpen(enabled: boolean): WebVirtualKeyboardState {
  const [state, setState] = useState<WebVirtualKeyboardState>(INITIAL_STATE);

  useEffect(() => {
    if (!enabled || Platform.OS !== "web" || typeof window === "undefined" || !window.visualViewport) {
      setState(INITIAL_STATE);
      return;
    }

    const vv = window.visualViewport;
    let rafId: number | null = null;

    const tick = () => {
      const overlap = readKeyboardOverlapPx();
      setState((prev) => {
        const nextOpen = prev.keyboardOpen
          ? overlap > CLOSE_THRESHOLD_PX
          : overlap > OPEN_THRESHOLD_PX;
        return { keyboardOpen: nextOpen, overlapPx: overlap };
      });
    };

    const tickRaf = () => {
      if (rafId !== null) {
        cancelAnimationFrame(rafId);
      }
      tick();
      rafId = requestAnimationFrame(() => {
        rafId = null;
        tick();
      });
    };

    tickRaf();
    vv.addEventListener("resize", tickRaf);
    vv.addEventListener("scroll", tickRaf);
    window.addEventListener("resize", tickRaf);

    return () => {
      if (rafId !== null) {
        cancelAnimationFrame(rafId);
        rafId = null;
      }
      vv.removeEventListener("resize", tickRaf);
      vv.removeEventListener("scroll", tickRaf);
      window.removeEventListener("resize", tickRaf);
    };
  }, [enabled]);

  return state;
}
